import { atom } from "jotai";
import { zapMetadataAtomFamily_SUSPENDS } from "./zapAtomFamily_SUSPENDS";
import { zapAtomFamily_SUSPENDS } from "./zapAtomFamily_SUSPENDS";
import { assetAtomFamily_SUSPENDS } from "../apps/assetAtomFamily_SUSPENDS";
import type { Zap } from "../types";

interface EnableZapParams {
  zapId: string;
  versionId: string;
}

interface ToggleZapParams {
  zapId: string;
  shouldEnable: boolean;
}

/**
 * Internal setter atom to enable a Zap by enabling a specific version
 * Calls custom API route that proxies to Gulliver with JWT auth
 * Invalidates cached Zap data after successful toggle to trigger UI refresh
 * @internal - Use toggleZapSetterAtom instead
 */
const enableZapSetterAtom = atom(
  null,
  async (_, __, { zapId, versionId }: EnableZapParams) => {
    const response = await fetch(`/api/canvasui/zaps/${zapId}/enable`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ versionId }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error ||
          errorData.detail ||
          `Failed to enable Zap: ${response.status}`,
      );
    }

    const result = await response.json();

    // Invalidate cached Zap data to force refetch and update UI
    zapMetadataAtomFamily_SUSPENDS.remove(zapId);
    zapAtomFamily_SUSPENDS.remove(zapId);
    assetAtomFamily_SUSPENDS.remove({
      assetType: "zap",
      assetId: zapId,
      interfaceProjectId: undefined,
    });

    return result;
  },
);

/**
 * Internal setter atom to disable (pause) a Zap
 * Calls custom API route that proxies to Gulliver with JWT auth
 * Invalidates cached Zap data after successful toggle to trigger UI refresh
 * @internal - Use toggleZapSetterAtom instead
 */
const disableZapSetterAtom = atom(null, async (_, __, zapId: string) => {
  const response = await fetch(`/api/canvasui/zaps/${zapId}/disable`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error ||
        errorData.detail ||
        `Failed to disable Zap: ${response.status}`,
    );
  }

  const result = await response.json();

  // Invalidate cached Zap data to force refetch and update UI
  zapMetadataAtomFamily_SUSPENDS.remove(zapId);
  zapAtomFamily_SUSPENDS.remove(zapId);
  assetAtomFamily_SUSPENDS.remove({
    assetType: "zap",
    assetId: zapId,
    interfaceProjectId: undefined,
  });

  return result;
});

/**
 * Setter atom to toggle a Zap's enabled/disabled state
 * Accesses zapDetails only when called to avoid unnecessary re-renders
 * Usage:
 * ```tsx
 * const toggleZap = useSetAtom(toggleZapSetterAtom);
 * await toggleZap({ zapId: "123", shouldEnable: true });
 * ```
 */
export const toggleZapSetterAtom = atom(
  null,
  async (get, set, { zapId, shouldEnable }: ToggleZapParams) => {
    if (shouldEnable) {
      // Get the current version ID from zapDetails only when enabling
      // Await the suspense atom to get the actual value
      const zapDetails = (await get(zapAtomFamily_SUSPENDS(zapId))) as Zap;
      const currentVersionId = zapDetails?.current_version?.id;

      if (!currentVersionId) {
        throw new Error("No version found to enable");
      }

      return set(enableZapSetterAtom, { zapId, versionId: currentVersionId });
    } else {
      return set(disableZapSetterAtom, zapId);
    }
  },
);
